import type { SubstitutionDto } from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { fmtDate } from '../components/fields';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { UserSelect } from '../lib/tasks-ui';
import { Empty, useToast } from '../lib/ui';

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Замещение на время отсутствия. В профиле — свой заместитель; у администратора
 * и делопроизводителя (all) — замещения всех сотрудников.
 */
export function SubstitutionsCard({ all = false }: { all?: boolean }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({ queryKey: ['substitutions', all], queryFn: () => api.get<SubstitutionDto[]>('/substitutions', { all }) });
  const [form, setForm] = useState({ userId: '', deputyId: '', dateFrom: today(), dateTo: today(), reason: '' });
  const create = useMutation({
    mutationFn: () =>
      api.post('/substitutions', {
        ...(all && form.userId ? { userId: form.userId } : {}),
        deputyId: form.deputyId,
        dateFrom: form.dateFrom,
        dateTo: form.dateTo,
        reason: form.reason || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['substitutions'] });
      setForm((f) => ({ ...f, deputyId: '', reason: '' }));
      toast('Заместитель назначен. Он получил уведомление.');
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/substitutions/${id}/cancel`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['substitutions'] });
      toast('Замещение отменено');
    },
    onError: (e) => toast(e.message, { error: true }),
  });
  const err = create.error instanceof ApiError ? create.error : null;
  const absent = all ? form.userId : user!.id;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <div className="card card-pad" style={{ display: 'grid', gap: 14 }}>
      <div>
        <h2>{all ? 'Замещения сотрудников' : 'Замещение на время отсутствия'}</h2>
        <p className="faint">
          Заместитель видит задачи отсутствующего в «Ждут меня» и может принимать решения за него. В истории задачи видно, кто решил.
          Согласованный отпуск с указанным замещающим создаёт замещение сам.
        </p>
      </div>

      {list.data && list.data.length === 0 && <Empty emoji="🪑" title="Замещений нет" />}
      {list.data && list.data.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <tbody>
              {list.data.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.user.id === user!.id && !all ? (
                      <>Вас замещает <b>{s.deputy.fullName}</b></>
                    ) : s.deputy.id === user!.id && !all ? (
                      <>Вы замещаете <b>{s.user.fullName}</b></>
                    ) : (
                      <><b>{s.user.fullName}</b> → {s.deputy.fullName}</>
                    )}
                    <br />
                    <span className="faint">
                      {fmtDate(s.dateFrom)} — {fmtDate(s.dateTo)}
                      {s.reason ? ` · ${s.reason}` : ''}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{s.active ? <span className="badge badge-brand">сейчас</span> : <span className="badge">запланировано</span>}</td>
                  <td style={{ textAlign: 'right' }}>
                    {(all || s.user.id === user!.id) && (
                      <button className="btn btn-ghost" disabled={cancel.isPending} onClick={() => confirm('Отменить замещение?') && cancel.mutate(s.id)}>
                        Отменить
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
        {err && !Object.keys(err.fields).length && <div className="alert">{err.message}</div>}
        <div className="form-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", maxWidth: "none" }}>
          {all && (
            <label className="field">
              <span>Кого замещают</span>
              <UserSelect value={form.userId} onChange={(userId) => setForm({ ...form, userId })} />
            </label>
          )}
          <label className="field">
            <span>Заместитель</span>
            <UserSelect value={form.deputyId} onChange={(deputyId) => setForm({ ...form, deputyId })} exclude={absent ? [absent] : []} invalid={Boolean(err?.fields.deputyId)} />
            {err?.fields.deputyId && <span className="field-error">{err.fields.deputyId}</span>}
          </label>
          <label className="field">
            <span>С</span>
            <input className="input" type="date" value={form.dateFrom} onChange={(e) => setForm({ ...form, dateFrom: e.target.value })} />
          </label>
          <label className="field">
            <span>По</span>
            <input className="input" type="date" value={form.dateTo} min={form.dateFrom} onChange={(e) => setForm({ ...form, dateTo: e.target.value })} aria-invalid={Boolean(err?.fields.dateTo)} />
            {err?.fields.dateTo && <span className="field-error">{err.fields.dateTo}</span>}
          </label>
          <label className="field">
            <span>Причина</span>
            <input className="input" value={form.reason} placeholder="Отпуск, командировка…" onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </label>
        </div>
        <div>
          <button className="btn btn-primary" disabled={create.isPending || !form.deputyId || (all && !form.userId)}>
            Назначить заместителя
          </button>
        </div>
      </form>
    </div>
  );
}

export function SubstitutionsPage() {
  return (
    <>
      <div className="page-head">
        <div>
          <h1>Замещения</h1>
          <p className="muted">Кто за кого принимает решения на время отпуска, болезни или командировки</p>
        </div>
      </div>
      <div style={{ maxWidth: 900 }}>
        <SubstitutionsCard all />
      </div>
    </>
  );
}
